/**
 * Google Calendar Integration Helpers
 *
 * Utilities for generating Google Calendar event URLs from volunteer slots.
 * Handles date formatting and event data preparation.
 */

import { removeClassPrefix, formatDayWithPrefix } from './slotHelpers'

/**
 * Volunteer slot interface for calendar generation
 */
interface VolunteerSlot {
  id: string
  date: string
  displayDate?: string
  day: string
  class_name: string
  volunteer_name: string | null
  time_slot: string
}

/**
 * Format a JavaScript Date object for Google Calendar URL
 *
 * Google Calendar requires dates in format: YYYYMMDDTHHmmssZ
 *
 * @param date - JavaScript Date object
 * @returns Formatted date string for Google Calendar
 *
 * @example
 * const date = new Date('2025-10-15T07:30:00')
 * formatDateForCalendar(date) // Returns: '20251015T073000Z'
 */
export const formatDateForCalendar = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/**
 * Generate a Google Calendar event URL for a volunteer slot
 *
 * Creates a pre-filled Google Calendar event with:
 * - Event title: "תורנות הורים - כיתה [class]"
 * - Start time: 07:30 AM
 * - End time: 08:10 AM (40 minutes duration)
 * - Location: Beit Sefer Ilanot Tel Aviv
 * - Description: Volunteer details
 *
 * @param slot - Volunteer slot data
 * @returns Google Calendar URL to create event
 *
 * @example
 * const slot = {
 *   date: '2025-10-15',
 *   day: 'ראשון',
 *   class_name: 'כיתה א-1',
 *   volunteer_name: 'דוד לוי',
 *   time_slot: '07:30-08:10'
 * }
 * const url = generateCalendarEventUrl(slot)
 * window.open(url, '_blank') // Opens Google Calendar with pre-filled event
 */
export const generateCalendarEventUrl = (slot: VolunteerSlot): string => {
  const volunteerName = slot.volunteer_name || 'מתנדב'
  const date = slot.displayDate || slot.date
  const className = slot.class_name

  // Convert Israeli date format (DD.MM.YYYY) to ISO date, or use database format (YYYY-MM-DD)
  let eventDate: Date
  if (date.includes('.')) {
    const [dayNum, month, year] = date.split('.')
    eventDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(dayNum))
  } else {
    // Database format YYYY-MM-DD
    eventDate = new Date(date)
  }

  // Set start time to 07:30 AM (Israeli school volunteer time)
  const startTime = new Date(eventDate)
  startTime.setHours(7, 30, 0, 0)

  // Set end time to 08:10 AM (40 minutes later)
  const endTime = new Date(eventDate)
  endTime.setHours(8, 10, 0, 0)

  // Prepare event details
  const title = encodeURIComponent(`תורנות הורים - כיתה ${removeClassPrefix(className)}`)
  const description = encodeURIComponent(
    `תורנות הורים\n` +
      `כיתה: ${removeClassPrefix(className)}\n` +
      `מתנדב: ${volunteerName}\n` +
      `יום: ${formatDayWithPrefix(slot.day)}\n` +
      `מיקום: בית ספר יסודי אילנות תל אביב\n` +
      `הערות: להגיע 5 דקות מוקדם`
  )
  const location = encodeURIComponent('בית ספר יסודי אילנות תל אביב')
  const startDate = formatDateForCalendar(startTime)
  const endDate = formatDateForCalendar(endTime)

  // Generate Google Calendar URL
  return (
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${title}` +
    `&dates=${startDate}/${endDate}` +
    `&details=${description}` +
    `&location=${location}`
  )
}
